// OneTrust ships via GTM (deferred), so window.Optanon is absent at mount. The
// OptanonConsent cookie is readable synchronously here and on the server, which
// is why consent is read from the cookie and never from the SDK.
const FUNCTIONAL_GROUP_ID = "C0003";

/** Dispatched on window by OneTrust when a visitor changes consent without reloading. */
export const CONSENT_CHANGED_EVENT = "OneTrustGroupsUpdated";

function readCookie(cookieString: string, name: string): string | undefined {
  for (const part of cookieString.split(";")) {
    const raw = part.trim();
    if (!raw.startsWith(`${name}=`)) continue;
    return raw.slice(name.length + 1);
  }
  return undefined;
}

export function hasFunctionalConsent(cookieString?: string): boolean {
  const cookies =
    cookieString ?? (typeof document === "undefined" ? "" : document.cookie);
  if (!cookies) return false;

  // The groups below are authoritative on their own: in opt-out geos OneTrust
  // grants C0003 on load, so requiring banner interaction would deny visitors
  // who have already consented. Do not re-add an OptanonAlertBoxClosed gate.
  const consent = readCookie(cookies, "OptanonConsent");
  if (!consent) return false;

  // Structure is derived exactly once, from the raw (undecoded) cookie value.
  // OptanonConsent carries a landingPath whose own encoded querystring — `&`,
  // `=`, even a literal "groups=" substring — would be indistinguishable from
  // real separators (and could smuggle in a decoy "groups" param) if the whole
  // value were decoded before being split into params. The raw split is safe:
  // an encoded `%26` inside landingPath's value can't act as a separator.
  const rawGroups = new URLSearchParams(consent).get("groups");
  if (!rawGroups) return false;

  // OneTrust only lists a group here when it's actually offering it as a
  // toggle. A GDPR session enumerates all four pre-interaction (functional
  // included, as C0003:0). An opt-out-geo session lists only the group(s) it
  // treats as an opt-out choice — the functional group is active by policy
  // there and never gets an entry at all. So an explicit value wins, but no
  // entry for the functional group means granted, not denied.
  //
  // The extracted value can still be under-decoded (a double-encoded cookie),
  // so it alone — never the surrounding structure — gets a second decode pass
  // to look for an explicit entry.
  for (const groups of [rawGroups, safeDecode(rawGroups)]) {
    if (groups === undefined) continue;
    const explicit = groups
      .split(",")
      .map((pair) => pair.trim())
      .find((pair) => pair.startsWith(`${FUNCTIONAL_GROUP_ID}:`));
    if (explicit !== undefined) return explicit === `${FUNCTIONAL_GROUP_ID}:1`;
  }

  return true;
}

function safeDecode(value: string): string | undefined {
  try {
    return decodeURIComponent(value);
  } catch {
    return undefined;
  }
}
